import React, { useCallback } from 'react';
import { Calculator, Zap } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрого расчета стоимости электроэнергии
 */
const QuickCalculationSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  russianRegions
}) => {
  // Получаем текущий регион
  const selectedRegion = russianRegions.find(r => r.id === formData.region) || russianRegions[0];
  
  // Расчет текущего потребления
  const getTotalConsumption = () => {
    if (formData.meterType === 'single') {
      return parseFloat(formData.totalConsumption) || 0;
    } else if (formData.tariffType === 'day_night') {
      return (parseFloat(formData.dayConsumption) || 0) + (parseFloat(formData.nightConsumption) || 0);
    } else {
      return (parseFloat(formData.peakConsumption) || 0) + 
             (parseFloat(formData.semipeakConsumption) || 0) + 
             (parseFloat(formData.offpeakConsumption) || 0);
    }
  };
  
  const totalConsumption = getTotalConsumption();
  
  // Обработчик изменения общего потребления
  const handleTotalConsumptionChange = useCallback((e) => {
    const newValue = e.target.value;
    const numValue = parseFloat(newValue) || 0;
    
    setFormData(prev => {
      // Если выбран однотарифный учет, просто изменяем общее потребление
      if (prev.meterType === 'single') {
        return { ...prev, totalConsumption: newValue };
      } 
      // Для многотарифных счетчиков, распределяем по зонам пропорционально текущему распределению
      else if (prev.tariffType === 'day_night') {
        const currentDay = parseFloat(prev.dayConsumption) || 0;
        const currentNight = parseFloat(prev.nightConsumption) || 0;
        const current = currentDay + currentNight;
        
        // Если текущее потребление 0, используем стандартное распределение
        const dayRatio = current > 0 ? currentDay / current : 0.7;
        const nightRatio = current > 0 ? currentNight / current : 0.3;
        
        return {
          ...prev,
          dayConsumption: (numValue * dayRatio).toFixed(0),
          nightConsumption: (numValue * nightRatio).toFixed(0)
        };
      }
      // Для трехзонного тарифа
      else {
        const currentPeak = parseFloat(prev.peakConsumption) || 0;
        const currentSemipeak = parseFloat(prev.semipeakConsumption) || 0;
        const currentOffpeak = parseFloat(prev.offpeakConsumption) || 0;
        const current = currentPeak + currentSemipeak + currentOffpeak;
        
        // Если текущее потребление 0, используем стандартное распределение
        const peakRatio = current > 0 ? currentPeak / current : 0.3;
        const semipeakRatio = current > 0 ? currentSemipeak / current : 0.5;
        const offpeakRatio = current > 0 ? currentOffpeak / current : 0.2;
        
        return {
          ...prev,
          peakConsumption: (numValue * peakRatio).toFixed(0),
          semipeakConsumption: (numValue * semipeakRatio).toFixed(0),
          offpeakConsumption: (numValue * offpeakRatio).toFixed(0)
        };
      }
    });
    
    // Очищаем ошибку если она есть
    if (errors.totalConsumption) {
      setFormData(prev => {
        const newErrors = { ...prev.errors };
        delete newErrors.totalConsumption;
        return { ...prev, errors: newErrors };
      });
    }
  }, [setFormData, errors, formData.meterType, formData.tariffType]);
  
  // Обработчик выбора региона
  const handleRegionChange = (e) => {
    const region = e.target.value;
    setFormData(prev => ({ ...prev, region }));
  };
  
  // Обработчик выбора типа тарифа
  const handleTariffTypeChange = (type) => {
    setFormData(prev => {
      const newState = { ...prev, tariffType: type };
      
      // Обновляем тип счетчика в зависимости от типа тарифа
      if (type === 'single') {
        newState.meterType = 'single';
      } else {
        newState.meterType = 'multi';
      }
      
      return newState;
    });
  };
  
  // Функция быстрого расчета
  const quickCalculate = () => {
    // Вызываем кнопку расчета в основной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Предопределенные значения потребления
  const predefinedConsumption = [100, 250, 500, 750];
  
  // Быстрый расчет примерной стоимости (без учета льгот)
  const getQuickEstimate = () => {
    let estimatedCost = 0;
    
    if (formData.meterType === 'single') {
      estimatedCost = totalConsumption * selectedRegion.singleRate;
    } else if (formData.tariffType === 'day_night') {
      const dayConsumption = parseFloat(formData.dayConsumption) || 0;
      const nightConsumption = parseFloat(formData.nightConsumption) || 0;
      estimatedCost = (dayConsumption * selectedRegion.dayRate) + 
                      (nightConsumption * selectedRegion.nightRate);
    } else {
      const peakConsumption = parseFloat(formData.peakConsumption) || 0;
      const semipeakConsumption = parseFloat(formData.semipeakConsumption) || 0;
      const offpeakConsumption = parseFloat(formData.offpeakConsumption) || 0;
      estimatedCost = (peakConsumption * selectedRegion.peakRate) + 
                      (semipeakConsumption * selectedRegion.semipeakRate) + 
                      (offpeakConsumption * selectedRegion.offpeakRate);
    }
    
    return estimatedCost;
  };
  
  // Примерная стоимость для отображения
  const estimatedCost = getQuickEstimate();
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-yellow-500" />
        Быстрый расчет стоимости
      </h3>
      
      <div className="space-y-5">
        {/* Регион */}
        <div>
          <label htmlFor="quickRegion" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Выберите регион
          </label>
          <select
            id="quickRegion"
            value={formData.region}
            onChange={handleRegionChange}
            className="form-select w-full"
          >
            {russianRegions.map(region => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Тип тарифа */}
        <div>
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип тарифа
          </span>
          <div className="grid grid-cols-3 gap-1">
            {[
              { id: 'single', name: 'Однотарифный', color: 'bg-gray-200 dark:bg-gray-700' },
              { id: 'day_night', name: 'День/Ночь', color: 'bg-indigo-200 dark:bg-indigo-800' },
              { id: 'zones', name: 'Трехзонный', color: 'bg-purple-200 dark:bg-purple-800' }
            ].map(tariff => (
              <button
                key={tariff.id}
                onClick={() => handleTariffTypeChange(tariff.id)}
                className={`p-2 rounded-lg border transition-colors text-xs text-center ${
                  formData.tariffType === tariff.id
                    ? 'bg-yellow-100 border-yellow-300 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-300'
                    : `${tariff.color} border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300`
                }`}
              >
                {tariff.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Общее потребление */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="quickConsumption" className="text-gray-700 dark:text-gray-300">
              Потребление (кВт⋅ч в месяц)
            </label>
            <span className="font-medium text-gray-900 dark:text-white inline-flex items-center">
              <Zap className="w-3.5 h-3.5 mr-1 text-yellow-500" />
              {formatNumber(totalConsumption || 0, 0)}
            </span>
          </div>
          <input
            type="range"
            id="quickConsumption"
            min="10"
            max="2000"
            step="10"
            value={totalConsumption}
            onChange={handleTotalConsumptionChange}
            aria-label="Потребление электроэнергии"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-yellow-500"
          />
          
          {/* Предустановленные значения потребления */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedConsumption.map(consumption => (
              <button
                key={consumption}
                type="button"
                onClick={() => handleTotalConsumptionChange({ target: { value: consumption.toString() } })}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${Math.abs(totalConsumption - consumption) < 0.01
                    ? 'bg-yellow-100 border-yellow-300 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {consumption} кВт⋅ч
              </button>
            ))}
          </div>
        </div>
        
        {/* Примерная оценка стоимости */}
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 text-center">
          <p className="text-yellow-700 dark:text-yellow-300 text-sm mb-1">Примерная стоимость:</p>
          <p className="text-2xl font-bold text-yellow-800 dark:text-yellow-200">
            {formatNumber(estimatedCost, 0)} ₽
          </p>
          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
            без учета льгот и доп. параметров
          </p>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={quickCalculate}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать точно
        </Button>
      </div>
    </div>
  );
};

export default QuickCalculationSidebar;